import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Card } from "../../../../ui/components/Card";
import { Button } from "../../../../ui/components/Button";
import { Callout } from "../../../../ui/components/Callout";
import { Text } from "../../../../ui/components/Text";
import { AuthBrand } from "../../../../ui/components/AuthBrand";
import * as authText from "../../../../ui/components/Auth/authTypography.style.css";
import { useAuth } from "../../../lib/auth/hooks";
import * as productAccessApi from "../../../lib/api/product-access";
import { getApiErrorMessage } from "../../../lib/api/api-error";
import {
  clearInviteToken,
  persistInviteContext,
} from "../../../lib/auth/invite-pending";
import {
  getProAppOrigin,
  hardNavigateOnce,
} from "../../../lib/auth/app-origins";

const TIER_LABELS: Record<string, string> = {
  view: "View Access",
  edit: "Edit Access",
  full: "Full Access",
};

export interface AcceptProductInvitePageProps {
  token?: string;
}

export function AcceptProductInvitePage({
  token: tokenFromSearch,
}: AcceptProductInvitePageProps) {
  const search = { token: tokenFromSearch };
  const navigate = useNavigate();
  const { user, isAuthenticated, accessToken, refreshUser } = useAuth();
  const acceptAttempted = React.useRef(false);
  const [loading, setLoading] = React.useState(true);
  const [preview, setPreview] =
    React.useState<productAccessApi.ProductInvitePreview | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [accepting, setAccepting] = React.useState(false);

  React.useEffect(() => {
    const inviteToken = search.token?.trim();
    if (!inviteToken) {
      setError("This invitation link is invalid.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    void productAccessApi
      .fetchProductInvitePreview(inviteToken)
      .then((result) => {
        setPreview(result);
        if (result.valid) {
          persistInviteContext(inviteToken, result.email);
        } else {
          persistInviteContext(inviteToken);
        }
        if (!result.valid) {
          clearInviteToken();
          setError(
            result.expired
              ? "This invitation has expired."
              : "This invitation is no longer valid.",
          );
        }
      })
      .catch((e) => {
        clearInviteToken();
        setError(getApiErrorMessage(e, "Failed to load invitation"));
      })
      .finally(() => setLoading(false));
  }, [search.token]);

  React.useEffect(() => {
    if (
      !preview?.valid ||
      !isAuthenticated ||
      !accessToken ||
      !user?.email ||
      !preview.email ||
      !search.token
    ) {
      return;
    }
    if (user.email.toLowerCase() !== preview.email.toLowerCase()) {
      return;
    }
    if (!user.emailVerified) {
      return;
    }
    if (acceptAttempted.current) return;
    acceptAttempted.current = true;
    setAccepting(true);
    void productAccessApi
      .acceptProductInvite(accessToken, search.token)
      .then(async (result) => {
        clearInviteToken();
        await refreshUser();
        const productId = (result as { productId?: string }).productId;
        const destination = productId
          ? `${getProAppOrigin()}/workspace/products/${productId}`
          : `${getProAppOrigin()}/workspace/products`;
        hardNavigateOnce(destination);
      })
      .catch((e) => {
        setError(getApiErrorMessage(e, "Failed to accept invitation"));
        acceptAttempted.current = false;
      })
      .finally(() => setAccepting(false));
  }, [
    preview,
    isAuthenticated,
    user,
    accessToken,
    refreshUser,
    search.token,
  ]);

  const emailMismatch =
    preview?.valid &&
    isAuthenticated &&
    user?.email &&
    preview.email &&
    user.email.toLowerCase() !== preview.email.toLowerCase();

  const loginRedirect =
    typeof window !== "undefined"
      ? `${window.location.origin}/accept-product-invite?token=${encodeURIComponent(search.token ?? "")}`
      : "/accept-product-invite";

  return (
    <Card maxWidth={480} className={authText.userSetupCard}>
      <AuthBrand />
      <div className={authText.setupStepPanelForm}>
        <Text level="h3" weight="bold" style={{ margin: "0 0 8px" }}>
          Product invitation
        </Text>

        {(loading || accepting) && (
          <Text level="body-medium" style={{ color: "#6B7280" }}>
            {accepting ? "Accepting invitation…" : "Loading invitation…"}
          </Text>
        )}

        {!loading && !accepting && error ? (
          <Callout variant="error">{error}</Callout>
        ) : null}

        {!loading && !accepting && preview?.valid ? (
          <>
            <Text level="body-medium" style={{ margin: 0 }}>
              You&apos;ve been invited to access a product with{" "}
              <strong>
                {TIER_LABELS[preview.tier ?? "view"] ?? "View Access"}
              </strong>
              {preview.email ? (
                <>
                  {" "}
                  (sent to <strong>{preview.email}</strong>)
                </>
              ) : null}
              .
            </Text>

            {emailMismatch ? (
              <Callout variant="error">
                You are signed in as {user?.email}, but this invite was sent to{" "}
                {preview.email}. Sign in with the invited email, or register a
                matching account.
              </Callout>
            ) : null}

            {isAuthenticated && !emailMismatch && !user?.emailVerified ? (
              <Text level="body-medium" style={{ color: "#6B7280" }}>
                Confirm your email to accept this invitation.
              </Text>
            ) : null}

            {!isAuthenticated ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  marginTop: 8,
                }}
              >
                <Button
                  color="primary"
                  variant="solid"
                  fullWidth
                  size="lg"
                  onClick={() =>
                    navigate({
                      to: "/register",
                      search: { email: preview.email },
                    })
                  }
                >
                  Create account
                </Button>
                <Button
                  color="neutral"
                  variant="outline"
                  fullWidth
                  size="lg"
                  onClick={() =>
                    navigate({
                      to: "/login",
                      search: { redirect: loginRedirect },
                    })
                  }
                >
                  Sign in
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </Card>
  );
}
